import { z } from 'zod';
/**
 * Báo cáo của một lượt chạy job nền — APM-SYSMON-b (docs/plan-system-monitor.md §3.1).
 *
 * `IJobHandler.run()` **có thể** trả giá trị này; `JobWorker` lưu nó vào cột
 * `job_queue.report` để màn hình "Luồng ngầm" trả lời được câu "lượt vừa rồi
 * áp dụng cho bao nhiêu dự án". Handler cũ trả `void` vẫn chạy — báo cáo là
 * tuỳ chọn, và một báo cáo sai hình dạng chỉ bị bỏ (kèm cảnh báo trong log),
 * không làm job thất bại: việc của job đã xong rồi.
 */
export const JobRunReportSchema = z.object({
    /** Số dự án lượt này đã xử lý. */
    projects: z.number().int().nonnegative().optional(),
    /** Số tổ chức lượt này đã xử lý. */
    orgs: z.number().int().nonnegative().optional(),
    /** Số đơn vị việc khác (hàng đã dọn, thông báo đã gửi…). */
    items: z.number().int().nonnegative().optional(),
    /** Một dòng tự do cho người vận hành đọc. */
    note: z.string().max(300).optional(),
});
/*
  ─── Màn hình "Luồng ngầm" — APM-SYSMON-c (docs/plan-system-monitor.md §3.2–§3.4) ───

  ⚠️ Không nhận asOf: đây là trạng thái **vận hành** hiện tại của tiến trình
  (lịch cron, nhịp tim trong RAM, hàng đợi đang chờ) — không phải dữ liệu
  nghiệp vụ có lịch sử để dựng lại. `job_queue` chỉ giữ kết cục của **lượt gần
  nhất** mỗi hàng và bị dọn sau 30 ngày, nên "luồng ngầm hôm 12/05" không có
  nguồn nào để trả lời đúng.
*/
/** 🟢 / 🟡 / 🔴 — quy tắc ở `BackgroundJobsService` (plan §3.2). */
export const BackgroundHealthSchema = z.enum(['green', 'amber', 'red']);
/**
 * Vì sao một loại job không xanh. Mã thay vì câu chữ để giao diện dịch được.
 *
 * - `dead_24h`      — có lượt `dead` trong 24 giờ qua (🔴)
 * - `overdue`       — job định kỳ lỡ ≥ 2 nhịp liền không có lượt thành công (🔴)
 * - `stale_running` — có lượt `running` quá ngưỡng mồ côi 15 phút (🔴)
 * - `late`          — job định kỳ lỡ đúng nhịp gần nhất (🟡)
 * - `retrying`      — lượt cuối lỗi, đang chờ thử lại (🟡)
 * - `slow`          — lượt cuối mất hơn 3× trung vị 7 ngày (🟡)
 */
export const BackgroundJobIssueSchema = z.enum([
    'dead_24h',
    'overdue',
    'stale_running',
    'late',
    'retrying',
    'slow',
]);
/**
 * Trạng thái của **một hàng** `job_queue`, dịch cho người đọc.
 *
 * `JobQueueService.fail()` trả job về `pending` để thử lại, nên `pending` của
 * DB mang hai nghĩa: `queued` (chưa chạy lần nào) và `retrying` (đã lỗi ít nhất
 * một lần, đang chờ backoff). Trạng thái `failed` cũ của enum DB cũng đọc thành
 * `retrying`.
 */
export const BackgroundJobRunStatusSchema = z.enum([
    'queued',
    'running',
    'succeeded',
    'retrying',
    'dead',
]);
/** Lịch cron của job định kỳ — đọc từ `SchedulerRegistry`. `null` ở job chạy theo sự kiện. */
export const BackgroundJobScheduleSchema = z.object({
    /** Biểu thức cron, ví dụ `0 01 * * *`. */
    cron: z.string(),
    nextAt: z.string().datetime().nullable(),
    /**
     * Lần cron bắn gần nhất **trong tiến trình này** — `null` sau mỗi lần khởi
     * động lại cho tới nhịp đầu tiên. Đừng đọc nó như "lần chạy cuối"; số đó ở
     * `last.finishedAt`.
     */
    lastFiredAt: z.string().datetime().nullable(),
    /** Khoảng giữa hai nhịp kế tiếp (ms) — đủ để nói "mỗi giờ" / "mỗi ngày". */
    everyMs: z.number().int().nonnegative().nullable(),
});
/** Lượt gần nhất của một loại job (hàng có `attempts > 0` mới nhất). */
export const BackgroundJobLastRunSchema = z.object({
    status: BackgroundJobRunStatusSchema,
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    report: JobRunReportSchema.nullable(),
    /** Lỗi của lượt, rút gọn 300 ký tự. */
    error: z.string().nullable(),
    attempts: z.number().int().nonnegative(),
});
/**
 * Thống kê 7 ngày theo `finished_at`.
 *
 * - `runs`   — số hàng kết thúc một lượt trong 7 ngày (mọi kết cục).
 * - `failed` — số hàng đã lỗi ít nhất một lần nhưng chưa `dead` (đang chờ thử
 *   lại, hoặc lỗi rồi mới thành công). Một hàng chỉ giữ lượt gần nhất, nên đây
 *   là số **hàng** có lỗi, không phải số **lần** lỗi.
 * - `dead`   — số hàng `dead`.
 * - `p50Ms`/`p95Ms`/`maxMs` — trên thời lượng các lượt **thành công**, tối đa
 *   2.000 lượt mới nhất mỗi loại; rank gần nhất (nearest-rank). `null` khi chưa
 *   có lượt nào.
 * - `avgProjects` — trung bình `report.projects` trên các lượt có báo số đó.
 */
export const BackgroundJobStatsSchema = z.object({
    runs: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    dead: z.number().int().nonnegative(),
    p50Ms: z.number().int().nonnegative().nullable(),
    p95Ms: z.number().int().nonnegative().nullable(),
    maxMs: z.number().int().nonnegative().nullable(),
    avgProjects: z.number().nonnegative().nullable(),
});
export const BackgroundJobRowSchema = z.object({
    jobType: z.string(),
    schedule: BackgroundJobScheduleSchema.nullable(),
    last: BackgroundJobLastRunSchema.nullable(),
    /** Lượt `succeeded` gần nhất — có thể cũ hơn `last` khi lượt cuối lỗi. */
    lastSucceededAt: z.string().datetime().nullable(),
    stats7d: BackgroundJobStatsSchema,
    health: BackgroundHealthSchema,
    /** Rỗng khi `health = 'green'`. */
    issues: z.array(BackgroundJobIssueSchema),
    queue: z.object({
        pending: z.number().int().nonnegative(),
        running: z.number().int().nonnegative(),
    }),
});
/**
 * Vòng lặp không đi qua hàng đợi (`JobWorker`, long poll Telegram) — nhịp tim
 * trong RAM của **tiến trình đang trả lời**. Mất khi khởi động lại là đúng ý.
 *
 * `health`: 🔴 khi quá 3 nhịp không đập (tính từ lúc đăng ký nếu chưa đập lần
 * nào); 🟡 khi sự kiện gần nhất là một lỗi; còn lại 🟢.
 */
export const BackgroundLoopSchema = z.object({
    name: z.string(),
    lastTickAt: z.string().datetime().nullable(),
    lastError: z.string().nullable(),
    lastErrorAt: z.string().datetime().nullable(),
    /** Nhịp mong đợi (ms); `null` khi vòng lặp chưa khai. */
    expectedEveryMs: z.number().int().positive().nullable(),
    health: BackgroundHealthSchema,
});
/** `GET /admin/background-jobs`. */
export const BackgroundJobsViewSchema = z.object({
    generatedAt: z.string().datetime(),
    jobs: z.array(BackgroundJobRowSchema),
    loops: z.array(BackgroundLoopSchema),
});
/** Một hàng `job_queue` cho danh sách lượt chạy. */
export const BackgroundJobRunSchema = z.object({
    id: z.string(),
    status: BackgroundJobRunStatusSchema,
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    attempts: z.number().int().nonnegative(),
    report: JobRunReportSchema.nullable(),
    /** Rút gọn 300 ký tự. */
    error: z.string().nullable(),
    orgId: z.string().nullable(),
});
/** `GET /admin/background-jobs/:jobType/runs`. */
export const BackgroundJobRunsSchema = z.object({
    jobType: z.string(),
    runs: z.array(BackgroundJobRunSchema),
});
export const BACKGROUND_JOB_RUNS_DEFAULT_LIMIT = 50;
export const BACKGROUND_JOB_RUNS_MAX_LIMIT = 200;
export const BackgroundJobRunsQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(BACKGROUND_JOB_RUNS_MAX_LIMIT)
        .default(BACKGROUND_JOB_RUNS_DEFAULT_LIMIT),
});
//# sourceMappingURL=background-jobs.js.map